<template>
  <div>
    <SettingsPagePanelHeader>
      <template #title>カレンダー連携設定</template>
    </SettingsPagePanelHeader>
    <SettingsTable>
      <!-- Google OAuth アプリ申請中なので、現時点で利用できる・利用したい 400F にのみ解放する -->
      <SettingsTableRow name="Googleカレンダー連携" v-if="viewer?.is400FOA">
        <div :class="$style.row">
          <FlatButton
            variant="secondary"
            size="medium"
            @click="handleGoogleClick"
            :disabled="
              connectedProvider != null &&
              connectedProvider !== OAuthProvider.Google
            "
          >
            <div :class="$style.googleButton">
              <!--
                   Google のガイドラインに則るために、アイコンを置く
                   https://about.google/brand-resource-center/guidance/apis/#using-our-icons
              -->
              <GoogleIcon :class="$style.googleButton__icon" />
              <span>
                {{ googleButtonText }}
              </span>
            </div>
          </FlatButton>
        </div>
        <p :class="$style.connectDescription">
          ※Google API
          から受信した情報の使用および他のアプリへの転送は、制限付き使用要件を含む<a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Servicesユーザーデータポリシー</a
          >に従うものとします。
        </p>
      </SettingsTableRow>

      <SettingsTableRow name="Outlook 予定表 連携">
        <div :class="$style.row">
          <p
            v-if="connectedProvider === OAuthProvider.Microsoft"
            :class="$style.connected"
          >
            連携済み
          </p>
          <div>
            <FlatButton
              variant="secondary"
              size="medium"
              @click="handleMicrosoftButtonClick"
              :disabled="
                connectedProvider != null &&
                connectedProvider !== OAuthProvider.Microsoft
              "
              >{{ microsoftButtonText }}
            </FlatButton>
            <p
              v-if="connectedProvider !== OAuthProvider.Microsoft"
              :class="$style.connectDescription"
            >
              Microsoft のサイトに移動します
            </p>
          </div>
        </div>
      </SettingsTableRow>
    </SettingsTable>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsTableRow from '../SettingsTable/SettingsTableRow.vue'
import { useMicrosoftAuthorizationForPlannerAccount } from '@/hooks/useMicrosoftAuthorization'
import { OAuthProvider } from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'
import { useGoogleAuthorizationForPlannerAccount } from '@/hooks/useGoogleAuthorization'
import GoogleIcon from './GoogleIcon.vue'
import { useFetchOnCalendarAuthViewQuery } from './CalendarAuthView.generated'
import { captureException as newrelicCaptureException } from '@/newrelic'

const route = useRoute('calendarSetting')

const viewQuery = useFetchOnCalendarAuthViewQuery()

const alert = useAlert()

const viewer = computed(() => viewQuery.result.value?.viewer)

const connectedProvider = computed(
  () => viewer.value?.staff.connectedThirdPartyProvider
)

const hasGoogleOAuth = computed(
  () => viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Google
)

const googleOAuthScopes = ['https://www.googleapis.com/auth/calendar.events']

const googleAuthorization =
  useGoogleAuthorizationForPlannerAccount(googleOAuthScopes)
googleAuthorization.onDone(() => {
  viewQuery.refetch()
  alert.success('Googleカレンダーと連携しました。')
})

const handleGoogleClick = async () => {
  try {
    if (hasGoogleOAuth.value) {
      await googleAuthorization.revoke()
      viewQuery.refetch()
      alert.success('Googleカレンダー連携を解除しました。')
    } else {
      googleAuthorization.requestAndSendCode()
    }
  } catch (e) {
    newrelicCaptureException(e)
    console.error(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const googleButtonText = computed(() =>
  viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Google
    ? '解除する'
    : '連携する'
)

const microsoftButtonText = computed(() =>
  viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Microsoft
    ? '解除する'
    : '連携する'
)

const msAuth = useMicrosoftAuthorizationForPlannerAccount(
  route.params.entranceGUID
)

const hasMicrosoftOAuth = computed(
  () =>
    viewer.value?.staff.connectedThirdPartyProvider === OAuthProvider.Microsoft
)

const handleMicrosoftButtonClick = async () => {
  try {
    if (hasMicrosoftOAuth.value) {
      await msAuth.revoke()
      viewQuery.refetch()
      alert.success('Outlook 予定表 連携を解除しました。')
    } else {
      const url = await msAuth.issueOAuthUrl()
      window.location.href = url
    }
  } catch (e) {
    newrelicCaptureException(e)
    console.error(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}
</script>

<style module lang="scss">
.row {
  display: flex;
  align-items: center;
  gap: 16px;

  &__submit {
    display: flex;
    margin-top: 16px;
  }
}

.connected {
  font-weight: bold;
}

.connectDescription {
  margin-top: 8px;
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;

  a {
    color: $color-text-and-object-low-emphasis;
    text-decoration: underline;
  }
}

.googleButton {
  display: flex;
  gap: 8px;
  align-items: center;

  &__icon {
    width: 20px;
    height: 20px;
  }
}
</style>
